import type { PublicMeasurementAveragesResponseRecord } from '@on3/api';
import { Check } from '@on3/ui-lib/components/Svg';
import clsx from 'clsx';
import { Text } from 'components/UI/Typography/Text';
import { useCallback, useMemo } from 'react';

import styles from './PlayerVitalAverages.module.scss';

export const PlayerVitalAverageItem = ({
  measurementKey,
  measurementName,
  currentPersonMeasurement,
  currentMeasurementVerified,
  top300Average,
  sort,
  top300Difference,
  combineDraftedAverage,
  combineDraftedDifference,
}: PublicMeasurementAveragesResponseRecord) => {
  const isHeight = measurementKey === 1;
  const isWeight = measurementKey === 2;
  const is40 = measurementKey === 6;

  /** For when the lowest number is first */
  const isLowest = sort === 'Lowest';

  const hasAllTop300Values =
    !!currentPersonMeasurement && !!top300Average && !!top300Difference;

  const hasAllDraftedValues =
    !!currentPersonMeasurement &&
    !!combineDraftedAverage &&
    !!combineDraftedDifference;

  /** Rounds all values accordingly, and changes Height from inches to feet, */
  const formattedValue = useCallback(
    (v: number) => {
      if (isHeight) {
        return `${Math.floor(v / 12)}-${(v % 12)
          .toFixed(0)
          .replace(/\.0$/, '')}`;
      }

      if (isWeight) {
        return Math.round(v);
      }

      return parseFloat(v.toFixed(1));
    },
    [isHeight, isWeight],
  );
  /**Highest Value of the three, this is for determining which column is at 100% height.   Used for normal sorting highest to lowest.  */
  const highest = Math.max(
    currentPersonMeasurement ?? 0,
    top300Average ?? 0,
    combineDraftedAverage ?? 0,
  );

  /**Lowest Value of the three, this is for determining which column is at 100% height.   Used for reverse sorting lowest to highest.  */
  const lowestValue = [
    currentPersonMeasurement,
    top300Average,
    combineDraftedAverage,
  ]
    .filter((num) => num !== 0)
    .reduce((min, num) => min && num && Math.min(min, num), Infinity);

  /**  Calculates height for Current Measurement.*/
  const height1 = useMemo(() => {
    if (measurementKey && currentPersonMeasurement) {
      if (isLowest && lowestValue) {
        return {
          height:
            (100 - (currentPersonMeasurement / lowestValue - 1) * 100).toFixed(
              2,
            ) + '%',
        };
      }

      return {
        height: ((currentPersonMeasurement / highest) * 100).toFixed(2) + '%',
      };
    }
    // Lowest Number Keys array doesn't need to be in this dependency since it's static.
  }, [
    currentPersonMeasurement,
    highest,
    isLowest,
    lowestValue,
    measurementKey,
  ]);

  /**  Calculates height for Top300Average Measurement.*/
  const height2 = useMemo(() => {
    if (measurementKey && top300Average) {
      if (isLowest && lowestValue) {
        return {
          height:
            (100 - (top300Average / lowestValue - 1) * 100).toFixed(2) + '%',
        };
      }

      return {
        height: ((top300Average / highest) * 100).toFixed(2) + '%',
      };
    }
    // Lowest Number Keys array doesn't need to be in this dependency since it's static.
  }, [highest, isLowest, lowestValue, measurementKey, top300Average]);

  /**  Calculates height for CombineDrafted Measurement.*/
  const height3 = useMemo(() => {
    if (measurementKey && combineDraftedAverage) {
      if (isLowest && lowestValue) {
        return {
          height:
            (100 - (combineDraftedAverage / lowestValue - 1) * 100).toFixed(2) +
            '%',
        };
      }

      return {
        height: ((combineDraftedAverage / highest) * 100).toFixed(2) + '%',
      };
    }
    // Lowest Number Keys array doesn't need to be in this dependency since it's static.
  }, [combineDraftedAverage, highest, isLowest, lowestValue, measurementKey]);

  /**Calculates the percential the current measurement is compared to the avg:(top300 or drafted) */
  const calculatePercentDifference = (
    current: number,
    avg: number,
    difference: number,
    className: keyof typeof styles,
  ) => {
    // isLowest reverses what a positive change is for when a lower number is considered positive
    const shouldSwap = isLowest ? current > avg : avg > current;
    const isPositive = shouldSwap ? false : true;

    //Use the absolute difference to calculate what percentage is from 100%
    const absoluteDifference = (Math.abs(difference) / avg) * 100;

    //If change is positive then add change to 100 otherwise subtract from 100
    const percentile = isPositive
      ? 100 + absoluteDifference
      : 100 - absoluteDifference;

    return (
      <span
        className={clsx(styles.difference, styles[className], {
          [styles.positive]: isPositive,
          [styles.negative]: !isPositive,
        })}
      >
        {percentile.toFixed(2)}%
      </span>
    );
  };

  return (
    <div className={styles.vitalContainer}>
      <div className={styles.vitalNameContainer}>
        <Text className={styles.vitalName} component="span">
          {measurementName}
          {is40 && '-time'}
        </Text>
        {currentMeasurementVerified && (
          <span className={styles.checkmarkContainer}>
            <Check className={styles.checkmark} />
          </span>
        )}
      </div>
      <div className={styles.barGraphsContainer}>
        <span
          className={clsx(styles.currentBar, styles.column)}
          style={height1}
        >
          {!!currentPersonMeasurement &&
            formattedValue(currentPersonMeasurement)}
        </span>
        <span className={clsx(styles.top300Bar, styles.column)} style={height2}>
          {!!top300Average && formattedValue(top300Average)}
        </span>

        {hasAllTop300Values &&
          calculatePercentDifference(
            currentPersonMeasurement,
            top300Average,
            top300Difference,
            'differenceOne',
          )}
        {!hasAllTop300Values && (
          <span className={clsx(styles.difference, styles.differenceOne)}>
            -%
          </span>
        )}

        <span
          className={clsx(styles.draftedBar, styles.column)}
          style={height3}
        >
          {!!combineDraftedAverage && formattedValue(combineDraftedAverage)}
        </span>

        {hasAllDraftedValues &&
          calculatePercentDifference(
            currentPersonMeasurement,
            combineDraftedAverage,
            combineDraftedDifference,
            'differenceTwo',
          )}
        {!hasAllDraftedValues && (
          <span className={clsx(styles.difference, styles.differenceTwo)}>
            -%
          </span>
        )}
      </div>
    </div>
  );
};
